import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import {
  canLocalEvaluateForTab,
  invalidateInactiveCrossClientResultTabs,
  selectCrossClientHasActions,
  selectCrossClientPaging,
  selectCrossClientResultsExecution,
  selectCrossClientSearch,
  selectCrossClientSelectedTab,
  setCrossClientMatchDistribution,
  setCrossClientPageResults,
  setCrossClientResultBuckets,
  tabToResultFilter,
} from '../../../store/crossClient/crossClientResults'
import {
  useLazyGetCrossClientMatchDistributionQuery,
  useLazyGetCrossClientQueryExecutionResultsQuery,
  useLazyGetCrossClientResultBucketsQuery,
} from '../../../store/crossClient/crossClient'
import { CrossClientQueryResultOrderBys } from '../../../models/CrossClient'

export interface CrossClientResultsRefresh {
  refreshAfterResultMutation: () => Promise<void>
}

export const useCrossClientResultsRefresh = (): CrossClientResultsRefresh => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const paging = useAppSelector(selectCrossClientPaging)
  const search = useAppSelector(selectCrossClientSearch)
  const hasActions = useAppSelector(selectCrossClientHasActions)

  const [triggerGetResults] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [triggerGetResultBuckets] = useLazyGetCrossClientResultBucketsQuery()
  const [triggerGetMatchDistribution] = useLazyGetCrossClientMatchDistributionQuery()

  const refreshAfterResultMutation = useCallback(async () => {
    if (!execution) {
      return
    }
    dispatch(invalidateInactiveCrossClientResultTabs())
    const tab = selectedTab
    const { match, status } = tabToResultFilter(tab)
    const orderBy = canLocalEvaluateForTab(tab, execution) ? CrossClientQueryResultOrderBys.NEXT_STEP : undefined
    const [response, buckets, distribution] = await Promise.all([
      triggerGetResults({
        accountId,
        executionId: execution.id,
        match,
        status,
        search: search.clientName ?? undefined,
        hasActions: hasActions ?? undefined,
        orderBy,
        include: ['bulkResolverAnswers', 'localResolverAnswers'],
        page: Math.max(paging.page - 1, 0),
        size: paging.size,
      }).unwrap(),
      triggerGetResultBuckets({ accountId, executionId: execution.id }).unwrap(),
      triggerGetMatchDistribution({ accountId, executionId: execution.id }).unwrap(),
    ])
    dispatch(setCrossClientPageResults({ tab, response }))
    dispatch(setCrossClientResultBuckets(buckets ?? []))
    if (distribution) {
      dispatch(setCrossClientMatchDistribution(distribution))
    }
  }, [
    dispatch,
    accountId,
    execution,
    selectedTab,
    paging.page,
    paging.size,
    search.clientName,
    hasActions,
    triggerGetResults,
    triggerGetResultBuckets,
    triggerGetMatchDistribution,
  ])

  return { refreshAfterResultMutation }
}
